import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/App';
import { Loader2, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';

/**
 * Handles the Emergent Google Auth redirect.
 *
 * Flow:
 *   1. The user clicks "Continue with Google" on /auth.
 *   2. We send them to https://auth.emergentagent.com/?redirect=<this_url>.
 *   3. Emergent appends `#session_id=...` to the redirect URL.
 *   4. This page reads the fragment, posts the session_id to our backend,
 *      receives our JWT, and lands the user on /chat.
 *
 * All UI states (loading, error, retry) are designed to feel calm and
 * trustworthy — auth failure messages MUST NOT look scary or technical.
 */
const GoogleAuthCallback = () => {
  const navigate = useNavigate();
  const { loginWithGoogleSession } = useAuth();
  const [status, setStatus] = useState('verifying');     // verifying | error
  const [errorMsg, setErrorMsg] = useState('');
  const ran = useRef(false);                              // StrictMode double-invoke guard

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;

    // Emergent puts the session_id in the URL fragment, not the query.
    const hash = window.location.hash || '';
    const params = new URLSearchParams(hash.startsWith('#') ? hash.slice(1) : hash);
    const sessionId = params.get('session_id');

    if (!sessionId) {
      setStatus('error');
      setErrorMsg("We couldn't find a Google session in the response. Please try signing in again.");
      return;
    }

    // Scrub the URL so the session id isn't sitting in the browser history.
    try {
      window.history.replaceState({}, document.title, '/auth/callback');
    } catch (_) { /* ignore */ }

    loginWithGoogleSession(sessionId)
      .then(() => navigate('/chat', { replace: true }))
      .catch((err) => {
        setStatus('error');
        setErrorMsg(err?.message || 'Google sign-in could not be completed.');
      });
  }, [loginWithGoogleSession, navigate]);

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-6" data-testid="google-auth-callback">
      <div className="w-full max-w-sm text-center">
        {status === 'verifying' ? (
          <>
            <Loader2 className="h-7 w-7 animate-spin mx-auto text-primary" data-testid="google-callback-loader" />
            <div className="mt-5 text-base font-medium">Signing you in…</div>
            <div className="mt-1 text-sm text-muted-foreground">
              This usually takes a second or two.
            </div>
          </>
        ) : (
          <>
            <AlertTriangle className="h-7 w-7 mx-auto text-amber-500" />
            <div className="mt-5 text-base font-medium">Couldn't complete sign-in</div>
            <div
              className="mt-1 text-sm text-muted-foreground"
              data-testid="google-callback-error"
            >
              {errorMsg}
            </div>
            <Button
              onClick={() => navigate('/auth', { replace: true })}
              className="mt-6 h-10 rounded-full px-6"
              data-testid="google-callback-retry-btn"
            >
              Back to sign in
            </Button>
          </>
        )}
      </div>
    </div>
  );
};

export default GoogleAuthCallback;
